import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CustomerService } from '../../../core/data-services/customer.service';
import { Customer } from '../../../core/data-models/customer.model';
import { CustomerRequest } from '../../../core/request-models/customer-request.model';
import { clearableFieldValue } from '../../../core/utils/drone-request-utils';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from '../../../shared/pipes';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-customer-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, NgxMaskDirective, PhoneFormatPipe, MaxLengthDirective, CharCounterDirective],
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss']
})
export class CustomerFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private customerService = inject(CustomerService);

  form!: FormGroup;
  isEditMode = false;
  customerId: string | null = null;
  isSaving = false;
  saveError: string | null = null;
  /** The customer being edited, so an emptied optional field is sent as `null`. */
  private original: Customer | null = null;

  // US States for dropdown
  states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  ngOnInit(): void {
    this.initForm();
    this.checkEditMode();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.email]],
      phone: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      notes: ['']
    });
  }

  private checkEditMode(): void {
    this.customerId = this.route.snapshot.paramMap.get('id');
    if (this.customerId) {
      this.isEditMode = true;
      void this.loadCustomer(this.customerId);
    }
  }

  private async loadCustomer(id: string): Promise<void> {
    const customer = this.customerService.getCustomerById(id) ?? (await this.customerService.fetchCustomerById(id));
    if (customer) {
      this.original = customer;
      this.form.patchValue({
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zip: customer.zip || '',
        notes: customer.notes || ''
      });
    } else {
      // Customer not found, redirect to list
      this.router.navigate(['/customers']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    void this.save(this.form.getRawValue());
  }

  private async save(formData: Record<string, string | null>): Promise<void> {
    this.isSaving = true;
    this.saveError = null;
    const dto = this.toRequest(formData);
    const saved = this.isEditMode && this.customerId
      ? await this.customerService.updateCustomer(this.customerId, dto)
      : await this.customerService.createCustomer(dto);
    this.isSaving = false;
    if (saved) {
      this.router.navigate(['/customers']);
    } else {
      this.saveError = this.customerService.error() ?? 'Failed to save customer';
    }
  }

  /** Optional fields: unchanged ones are not sent, and one the user emptied is sent as `null` to clear it. */
  private toRequest(formData: Record<string, string | null>): CustomerRequest {
    const optional = (key: keyof CustomerRequest) =>
      clearableFieldValue(formData[key], (this.original?.[key] as string | null | undefined) ?? undefined);
    return {
      name: formData['name']?.trim() || undefined,
      email: optional('email'),
      phone: optional('phone'),
      address: optional('address'),
      city: optional('city'),
      state: optional('state'),
      zip: optional('zip'),
      notes: optional('notes')
    };
  }

  onCancel(): void {
    this.router.navigate(['/customers']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  openInMaps(): void {
    const address = this.form.get('address')?.value;
    const city = this.form.get('city')?.value;
    const state = this.form.get('state')?.value;
    const zip = this.form.get('zip')?.value;

    const fullAddress = [address, city, state, zip].filter(Boolean).join(', ');
    if (fullAddress) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(url, '_blank');
    }
  }
}
